import {
  buildCost,
  EXPLORE_RULES,
  JOB_RULES,
  RESOURCE_BUILDING_RULES,
  buildingEffects,
  economyEffects,
  CLEARING_RULES,
  clearToolFor,
  fuelCost,
  HOME_BASE_RULES as RULES,
  freeSpots,
  GAME_DATA,
  hexKey,
  houseTile,
  hexSpiral,
  inSeason,
  isBuildable,
  removeRefund,
  shortfall,
  tileUseRefusal,
  upgradeCost,
  type Building,
  type BuildingEffect,
  type BuildingSlot,
  type EconomyEffect,
  type HomeResponse,
  type HomeSquishy,
  type MyBuilding,
  type Rarity,
  type Species,
} from '@heartpatch/shared';
import { itemName } from '../inventory/bag-view.js';
import { itemIcon } from '../inventory/item-icons.js';
import { ELEMENT_GLYPH } from '../ui/glyphs.js';

// What the home-base screen says and offers (copy follows docs/STYLE_GUIDE.md).
// Pure, so every case is unit-tested; the server decides everything again
// when the player taps (CLAUDE.md rule 1).

export const BUILDING_DATA = new Map<string, Building>(GAME_DATA.buildings.map((b) => [b.id, b]));
const SEASON_NAMES = new Map(GAME_DATA.seasons.map((s) => [s.id, s.name]));
const ELEMENT_NAMES = new Map(GAME_DATA.elements.map((e) => [e.id, e.name]));
const FEELING_NAMES = new Map(GAME_DATA.feelings.map((f) => [f.id, f.name]));

/** Picture for a building on buttons and cards. */
export function buildingIcon(buildingId: string): string {
  switch (buildingId) {
    case 'hearthfire':
      return '🔥';
    case 'jack-o-lantern-hearthfire':
      return '🎃';
    case 'joy-lantern':
      return '🕯️';
    case 'ember-den':
      return '🛖';
    case 'cozy-meadow':
      return '🌼';
    case 'training-grounds':
      return '🎯';
    case 'crafting-factory':
      return '🏭';
    // Resource buildings (#601): what each one works.
    case 'woodcutters-hut':
      return '🌲';
    case 'ember-kiln':
      return '🏺';
    case 'stone-quarry':
      return '🧱';
    case 'ice-cutter':
      return '🏔️';
    case 'water-well':
      return '⛲';
    case 'greenhouse':
      return '🥬';
    case 'pumpkin-barn':
      return '🥧';
    // The Warehouse (#601 PR 9): it delivers to your bag.
    case 'warehouse':
      return '📦';
    // Wild Isle (#621 W1-10): the works, canal, shelters and vane.
    case 'river-well':
      return '💧';
    case 'fishing-pier':
      return '🎣';
    case 'salt-pans':
      return '🧂';
    case 'watermill':
      return '🌾';
    case 'irrigated-field':
      return '🌱';
    case 'rain-barrel':
      return '🛢️';
    case 'canal':
      return '🏞️';
    case 'levee':
      return '🌿';
    case 'seawall':
      return '🌊';
    case 'weather-vane':
      return '🧭';
    default:
      return '🏠';
  }
}

export function buildingName(buildingId: string): string {
  return BUILDING_DATA.get(buildingId)?.name ?? 'Mystery building';
}

/** "5 🪵 Timber, 5 🪨 Stone" for a cost. */
export function costText(cost: Record<string, number>): string {
  return Object.entries(cost)
    .map(([id, n]) => `${String(n)} ${itemIcon(id)} ${itemName(id)}`)
    .join(', ');
}

/** Is this building on my home base (not out on my captured land, #202)? */
export function isAtHome(
  home: Pick<HomeResponse, 'tiles'>,
  b: Pick<MyBuilding, 'q' | 'r'>,
): boolean {
  return home.tiles.some((t) => t.q === b.q && t.r === b.r);
}

/**
 * The home as the home screen shows it: only the buildings on the home base.
 * Fires out on my land live on their tiles (the map's tile panel, #202).
 */
export function atHome(home: HomeResponse): HomeResponse {
  return { ...home, buildings: home.buildings.filter((b) => isAtHome(home, b)) };
}

/** My fires out on captured land (#202), in the server's order. */
export function landFires(home: HomeResponse): MyBuilding[] {
  return home.buildings.filter((b) => b.kind === 'hearthfire' && !isAtHome(home, b));
}

/** What "Fuel all fires" would do now (#202), or null with no fire out on my land. */
export interface FuelAllOffer {
  /** Fires out on my land. */
  readonly land: number;
  /** My fires with a night or less left (all of them, as the server's Fuel all counts; after the boot pass they're all on land). */
  readonly low: number;
  /** Emberwood (and any other fuel) to fill every fire. */
  readonly cost: Record<string, number>;
  /** Every fire is full already. */
  readonly full: boolean;
}

export function fuelAllOffer(home: HomeResponse): FuelAllOffer | null {
  const outside = landFires(home);
  if (outside.length === 0) return null;
  const fires = home.buildings.filter((b) => b.kind === 'hearthfire');
  const cost: Record<string, number> = {};
  for (const b of fires) {
    const fire = BUILDING_DATA.get(b.buildingId);
    if (fire?.kind !== 'hearthfire' || (b.fuelSpace ?? 0) === 0) continue;
    for (const [id, n] of Object.entries(fuelCost(fire, b.fuelSpace ?? 0))) {
      cost[id] = (cost[id] ?? 0) + n;
    }
  }
  return {
    land: outside.length,
    low: fires.filter((b) => (b.nightsLeft ?? 0) <= 1).length,
    cost,
    full: Object.keys(cost).length === 0,
  };
}

/** What the tile panel offers on one of my tiles out on captured land (#202). */
export type LandTileOffer =
  | { readonly kind: 'fire'; readonly fire: MyBuilding }
  /** Its middle holds a node: a fire next door can reach it. */
  | { readonly kind: 'node'; readonly line: string }
  | {
      readonly kind: 'build';
      readonly building: Building;
      readonly needs: readonly NeedChip[];
      /** The Jack-o'-Lantern Hearthfire too, in season with a carved pumpkin in the bag. */
      readonly lantern: { readonly building: Building; readonly needs: readonly NeedChip[] } | null;
    }
  /** Something else stands in the middle (not one of mine to show here). */
  | { readonly kind: 'none' };

/** What stands in a tile's middle, in a kid's words (never "node", owner note 2026-10-07). */
const MIDDLE_THINGS: Readonly<Record<string, string>> = {
  treats: 'a farm plot',
  pumpkins: 'a pumpkin patch',
  'magic-fallen-leaves': 'a leaf pile',
  glimmer: 'some Glimmer crystals',
  // #238
  water: 'a little well',
  greens: 'a patch of greens',
  ice: 'some ice crystals',
};

/** "a Timber pile", "a farm plot": the resource spot in a tile's middle. */
export function middleThing(resource: string): string {
  return MIDDLE_THINGS[resource] ?? `a ${itemName(resource)} pile`;
}

/** The building that can stand on owned land, one a tile, in its middle: the Hearthfire. */
export const LAND_FIRE = GAME_DATA.buildings.find(
  (b) => b.placement === 'land' && b.kind === 'hearthfire',
);

/**
 * What the spot line adds when a tool clears this land (#242): clear it on a
 * homestead, or join it up first. Empty on land no tool clears, and on a
 * napping homestead.
 */
export function clearHint(tile: {
  terrain?: string;
  homestead?: 'joined' | 'paused' | null;
}): string {
  const id = tile.terrain ? clearToolFor(tile.terrain, CLEARING_RULES) : null;
  const tool = id ? CLEARING_RULES.tools.find((t) => t.id === id)?.name : undefined;
  // A napping homestead wakes up first; its header and panel say how.
  if (!tool || tile.homestead === 'paused') return '';
  const a = /^[aeiou]/i.test(tool) ? 'an' : 'a';
  return tile.homestead === 'joined'
    ? ` Or clear it with ${a} ${tool} and build here 🌱`
    : ` Once it's a homestead, ${a} ${tool} can clear it 🌱`;
}

export function landTileOffer(
  tile: {
    q: number;
    r: number;
    nodeResource: string | null;
    buildings: readonly { spot: number }[];
    terrain?: string;
    homestead?: 'joined' | 'paused' | null;
  },
  home: HomeResponse,
): LandTileOffer {
  const fire = home.buildings.find(
    (b) => b.kind === 'hearthfire' && b.q === tile.q && b.r === tile.r,
  );
  if (fire) return { kind: 'fire', fire };
  if (!LAND_FIRE) return { kind: 'none' };
  if (tile.nodeResource !== null) {
    return {
      kind: 'node',
      line: `🔥 Fires go in the middle of a tile. This one has ${middleThing(tile.nodeResource)} there, so a fire next door can reach it!${clearHint(tile)}`,
    };
  }
  // One building a tile (#295).
  if (tile.buildings.length > 0) return { kind: 'none' };
  const seasons = new Set(home.seasons);
  const lantern =
    GAME_DATA.buildings.find(
      (b) =>
        b.placement === 'land' &&
        b.id !== LAND_FIRE.id &&
        // A Wild Isle Bridge (#621) is `land` too, but never on a Cozy Patch.
        isBuildable(RULES, b) &&
        inSeason(b, seasons) &&
        Object.keys(shortfall(home.items, buildCost(b))).length === 0,
    ) ?? null;
  return {
    kind: 'build',
    building: LAND_FIRE,
    needs: needChips(home.items, buildCost(LAND_FIRE)),
    lantern: lantern
      ? { building: lantern, needs: needChips(home.items, buildCost(lantern)) }
      : null,
  };
}

/** Training Grounds: they stand only on a homestead (owner decision 4 on #277). */
export const GROUNDS = GAME_DATA.buildings.find((b) => b.kind === 'training-grounds');

/** The Crafting Factory: on a homestead since #295 (one building a tile), one per player. */
export const FACTORY = GAME_DATA.buildings.find((b) => b.kind === 'factory');

/** Where a homestead building goes on an empty homestead: its first free spot. */
export function homesteadSpot(
  tile: { nodeResource: string | null; buildings: readonly { spot: number }[] },
  building: Pick<Building, 'slot'>,
): number | undefined {
  const taken = new Set(tile.buildings.map((b) => b.spot));
  return freeSpots(
    RULES,
    { heartSeed: false, nodeResource: tile.nodeResource },
    taken,
    building.slot,
  )[0];
}

/** What a homestead's tile panel and Build sheet offer for the Crafting Factory (#295). */
export type FactoryTileOffer =
  /** Mine stands here: its card opens it. */
  | { readonly kind: 'factory'; readonly factory: MyBuilding }
  /** An empty, open homestead and no Factory yet: build it here. */
  | { readonly kind: 'build'; readonly building: Building; readonly spot: number };

/**
 * The Crafting Factory on one of my tiles out on the land (#295): mine
 * standing there, or the offer to build my one Factory on an empty, joined
 * homestead with no spot to clear. Null otherwise.
 */
export function factoryTileOffer(
  tile: {
    q: number;
    r: number;
    nodeResource: string | null;
    homestead?: 'joined' | 'paused' | null;
    buildings: readonly { spot: number }[];
  },
  home: HomeResponse,
): FactoryTileOffer | null {
  const factory = home.buildings.find(
    (b) => b.kind === 'factory' && b.q === tile.q && b.r === tile.r,
  );
  if (factory) return { kind: 'factory', factory };
  if (!FACTORY || tile.homestead !== 'joined') return null;
  if (tile.buildings.length > 0 || tile.nodeResource !== null) return null;
  if (home.buildings.some((b) => b.kind === 'factory')) return null;
  const spot = homesteadSpot(tile, FACTORY);
  return spot === undefined ? null : { kind: 'build', building: FACTORY, spot };
}

/** What a homestead's tile panel offers for Training Grounds (#277, mockup screen 6). */
export type GroundsTileOffer =
  | { readonly kind: 'grounds'; readonly grounds: MyBuilding; readonly napping: boolean }
  | {
      readonly kind: 'build';
      readonly building: Building;
      readonly needs: readonly NeedChip[];
      /** The first free ring spot on the tile. */
      readonly spot: number;
    }
  /** A homestead cut off from home: nothing new goes up until it joins again. */
  | { readonly kind: 'napping' }
  /** A resource spot stands here: it goes up once the land is cleared (#242). */
  | { readonly kind: 'clear-first'; readonly building: Building };

/**
 * Training Grounds on one of my tiles out on the land: the ones standing
 * there, or the offer to build one on an empty homestead (one building a
 * tile, #295). A homestead with a resource spot is cleared first (#242).
 * Null: not a homestead, or something else stands there.
 */
export function groundsTileOffer(
  tile: {
    q: number;
    r: number;
    terrain: string;
    nodeResource: string | null;
    homestead?: 'joined' | 'paused' | null;
    buildings: readonly { spot: number }[];
  },
  home: HomeResponse,
): GroundsTileOffer | null {
  const napping = tile.homestead === 'paused';
  const grounds = home.buildings.find(
    (b) => b.kind === 'training-grounds' && b.q === tile.q && b.r === tile.r,
  );
  if (grounds) return { kind: 'grounds', grounds, napping };
  if (!GROUNDS || !tile.homestead) return null;
  if (napping) return { kind: 'napping' };
  if (tile.buildings.length > 0) return null;
  if (tile.nodeResource !== null) {
    // Land no tool clears keeps its spot: nothing goes up there.
    return clearToolFor(tile.terrain, CLEARING_RULES)
      ? { kind: 'clear-first', building: GROUNDS }
      : null;
  }
  const spot = homesteadSpot(tile, GROUNDS);
  if (spot === undefined) return null;
  return {
    kind: 'build',
    building: GROUNDS,
    needs: needChips(home.items, buildCost(GROUNDS)),
    spot,
  };
}

/** The home's top line (owner decision 2026-10-07): the Heart Seed keeps home safe. */
export const HOME_SAFE_LINE = 'Your Heart Seed keeps home safe 💗';

/** The Home build sheet's one line about fires (they stand only on captured land): no card. */
export const FIRES_ON_LAND = 'Fires go out on your land: tap a tile on the map 🔥';

/** No free tile left at home (#537): one building per tile (#295), and every one is taken. */
export const HOME_FULL = 'Your home base is full! Take down a building to make room 🏡';

/**
 * Every home building is up to its cap (#537), and one can still grow. Taps on
 * buildings wait while the Build sheet is up, so Not now comes first.
 */
export const HOME_ALL_BUILT =
  'Every squishy home is built! Tap Not now, then tap one to upgrade ⬆️';

/**
 * No free tile at home, but a home building can still grow (owner 2026-10-18,
 * with the 4-per-home cap): point to upgrades before "take one down".
 */
export const HOME_FULL_GROW = 'Your home base is full! Tap Not now, then tap one to upgrade ⬆️';

/** Every home building is up to its cap, each at its top level (#537). */
export const HOME_ALL_TOP = 'Every squishy home is built and as big as it gets! 🏆';

/**
 * One ingredient as have/need ("🪵 12/5"), so a kid sees at a glance what's
 * missing (design review 2026-10-05: one cost line, as icon counts).
 */
export interface NeedChip {
  readonly id: string;
  readonly icon: string;
  readonly have: number;
  readonly need: number;
  /** Enough in the bag. */
  readonly ok: boolean;
  /** "🪵 12/5", with the item's name for screen readers. */
  readonly label: string;
  readonly name: string;
}

/** Have/need chips for a cost, in the cost's order. */
export function needChips(
  items: Readonly<Record<string, number>>,
  cost: ItemCountsLike,
): NeedChip[] {
  return Object.entries(cost).map(([id, need]) => {
    const have = items[id] ?? 0;
    return {
      id,
      icon: itemIcon(id),
      have,
      need,
      ok: have >= need,
      label: `${itemIcon(id)} ${String(have)}/${String(need)}`,
      name: itemName(id),
    };
  });
}

type ItemCountsLike = Readonly<Record<string, number>>;

/** How a crafted ingredient is made, for "Carve a Jack-o'-Lantern first!" (default "Make"). */
const CRAFT_VERBS: Readonly<Record<string, string>> = { 'jack-o-lantern-hearthfire': 'Carve' };

/** What tapping "Build" on one building would do, and why not if it can't. */
export type BuildOption =
  | { readonly kind: 'ready' }
  /** Short of a gathered ingredient: Build shows, switched off; the chips say what. */
  | { readonly kind: 'short' }
  /** Short of something you make (the Jack-o'-Lantern): make it in the recipe book first. */
  | { readonly kind: 'craft'; readonly note: string }
  /** Already built (as many as the home base holds): upgrade it at home instead. */
  | { readonly kind: 'built'; readonly note: string }
  | { readonly kind: 'blocked'; readonly note: string };

export interface BuildRow {
  readonly building: Building;
  readonly icon: string;
  /** What it's for, in a line (#207). */
  readonly description: string;
  /** What it does, as chips worked out from its data (#207): "🛡️ Safe 1 tile around". */
  readonly effects: readonly string[];
  readonly needs: readonly NeedChip[];
  readonly option: BuildOption;
  /** Which spots it takes (#204): "Fires go in the middle of a tile 🔥". */
  readonly where: string;
}

/** One effect as a build-menu chip (#207; owner-approved words, 2026-10-07). */
export function effectChip(effect: BuildingEffect): string {
  switch (effect.kind) {
    case 'safe':
      return effect.radius === 0
        ? '🛡️ Keeps its own tile safe'
        : `🛡️ Safe ${String(effect.radius)} ${effect.radius === 1 ? 'tile' : 'tiles'} around`;
    case 'fuel':
      return '🪵 Needs fuel each night';
    case 'grows': {
      const who = [
        ...effect.elements.map((e) => `${ELEMENT_GLYPH[e] ?? '✨'} ${ELEMENT_NAMES.get(e) ?? e}`),
        ...effect.feelings.map((f) => FEELING_NAMES.get(f) ?? f),
      ];
      return `✨ Faster growing: ${who.join(' · ')}`;
    }
    case 'room':
      return `🏠 Room for ${String(effect.capacity)}`;
    case 'training':
      return `🏋️ ${String(effect.capacity)} ${effect.capacity === 1 ? 'squishy' : 'squishies'} · ${String(effect.xpPerHour)} XP/hr`;
    case 'fence':
      return `🪵 Keeps other Keepers out · ${String(effect.hp)} energy`;
    case 'batches':
      return `🏭 ${String(effect.queues)} batches at once`;
  }
}

/** A building's chips at a level (1: just built), for the build menu and the fire sheet. */
export function effectChips(building: Building, level = 1): string[] {
  return [
    ...buildingEffects(building, level).map(effectChip),
    ...economyEffects(building, level, ECONOMY_RULES).flatMap(economyChips),
  ];
}

const ECONOMY_RULES = {
  jobs: JOB_RULES,
  explore: EXPLORE_RULES,
  resourceBuildings: RESOURCE_BUILDING_RULES,
};

/** A resource building's or the Warehouse's chips (#601): what it makes, and its morning upkeep. */
export function economyChips(effect: EconomyEffect): string[] {
  const upkeep = `💛 ${String(effect.upkeep)} each morning`;
  if (effect.kind === 'delivers') return ['📦 Delivers to your Bag', upkeep];
  return [
    `${itemIcon(effect.resource)} ${String(effect.perHour)} ${itemName(effect.resource)} an hour`,
    upkeep,
  ];
}

/** Where a building goes on a tile, in a line (#204). */
export function slotLine(building: Pick<Building, 'kind' | 'slot'>): string {
  if (building.slot === 'centre') {
    return building.kind === 'hearthfire'
      ? 'Fires go in the middle of a tile 🔥'
      : 'Goes in the middle of a tile';
  }
  if (building.slot === 'ring') return 'Goes around the middle 🏡';
  return 'Goes along the edge of a tile';
}

const isCrafted = (id: string) => GAME_DATA.resources.find((r) => r.id === id)?.kind === 'crafted';

/**
 * The Home build menu: every building that goes at home (the habitats), in
 * data order. Fires and homestead buildings are built out on the land from the
 * map's tile panel, so they have no card here.
 */
export function buildRows(everything: HomeResponse): BuildRow[] {
  const home = atHome(everything);
  const seasons = new Set(home.seasons);
  return (
    GAME_DATA.buildings
      .filter((b) => isBuildable(RULES, b))
      .filter((b) => b.placement === 'home')
      // Fences (#203) go on a tile's edges, from the tile's panel on the map.
      .filter((b) => b.slot !== 'edge')
      .filter((b) => inSeason(b, seasons) || home.buildings.some((m) => m.buildingId === b.id))
      .map((building) => {
        const cost = buildCost(building);
        const owned = home.buildings.filter((b) => b.buildingId === building.id).length;
        const short = Object.keys(shortfall(home.items, cost));
        const crafted = short.find(isCrafted);
        let option: BuildOption = { kind: 'ready' };
        if (owned >= (building.maxPerHome ?? 0)) {
          option = {
            kind: 'built',
            note: building.levels.length > 1 ? 'Built! Tap it at home to upgrade.' : 'Built!',
          };
        } else if (!inSeason(building, seasons)) {
          const season = SEASON_NAMES.get(building.season ?? '') ?? 'its season';
          option = { kind: 'blocked', note: `Only around ${season}.` };
        } else if (freeHomeTiles(home, null, building.slot).length === 0) {
          // Before what it costs: gathering more wouldn't make room (#537).
          option = { kind: 'blocked', note: HOME_FULL };
        } else if (crafted) {
          const verb = CRAFT_VERBS[crafted] ?? 'Make';
          option = {
            kind: 'craft',
            note: `${verb} a ${itemName(crafted)} first! It's in your recipe book.`,
          };
        } else if (short.length > 0) {
          option = { kind: 'short' };
        }
        return {
          building,
          icon: buildingIcon(building.id),
          description: building.description,
          effects: effectChips(building),
          needs: option.kind === 'built' ? [] : needChips(home.items, cost),
          option,
          where: slotLine(building),
        };
      })
  );
}

/**
 * Why nothing more goes up at home (#537), for the top of the Build sheet, or
 * null while a home building could still go up (gathering or carving aside).
 * Home buildings at their cap have no card, so without this line a kid sees
 * a near-empty picker and no reason.
 */
export function homeBuildReason(
  everything: HomeResponse,
  rows: readonly BuildRow[] = buildRows(everything),
): string | null {
  const seasons = new Set(everything.seasons);
  const home = rows.filter((r) => r.building.placement === 'home' && inSeason(r.building, seasons));
  const full = (r: BuildRow) => r.option.kind === 'blocked' && r.option.note === HOME_FULL;
  if (home.length === 0 || !home.every((r) => r.option.kind === 'built' || full(r))) return null;
  const ids = new Set(home.map((r) => r.building.id));
  const grows = atHome(everything).buildings.some(
    (b) => ids.has(b.buildingId) && upgradeOffer(everything, b) !== null,
  );
  if (home.some(full)) return grows ? HOME_FULL_GROW : HOME_FULL;
  return grows ? HOME_ALL_BUILT : HOME_ALL_TOP;
}

/** What upgrading one of my buildings would do (owner decision 2026-10-06). */
export interface UpgradeOffer {
  readonly building: Building;
  readonly from: number;
  readonly to: number;
  readonly needs: readonly NeedChip[];
  readonly affordable: boolean;
  /** What the new level does, in a line ("Its light will reach 2 tiles…"). */
  readonly line: string;
  /** A Hearthfire's radius at the new level (its light on the land), else null. */
  readonly radius: number | null;
  /** What the level after needs, if it needs something new ("…needs 💎 Glimmer."). */
  readonly next: string | null;
}

/** The upgrade on offer for a building, or null at its top level (or for one-level buildings). */
export function upgradeOffer(home: HomeResponse, b: MyBuilding): UpgradeOffer | null {
  const building = BUILDING_DATA.get(b.buildingId);
  if (!building) return null;
  const cost = upgradeCost(building, b.level);
  const step = building.levels[b.level];
  if (!cost || !step) return null;
  const needs = needChips(home.items, cost);
  const to = b.level + 1;
  let line = '';
  let radius: number | null = null;
  const tiles = (n: number) => (n === 1 ? '1 tile' : `${String(n)} tiles`);
  if ('safeRadius' in step) {
    radius = step.safeRadius;
    const now = building.levels[b.level - 1];
    const after = building.levels[to];
    // Radii 1 / 1 / 2 (#277): level 2 is the step to a wider glow.
    line =
      now && 'safeRadius' in now && now.safeRadius === radius && after && 'safeRadius' in after
        ? `Same glow for now. At level ${String(to + 1)} it reaches ${tiles(after.safeRadius)}!`
        : `Its light will reach ${tiles(radius)}. Squishies out there stay safe at night!`;
  } else if ('queues' in step) {
    line = `Room for ${String(step.queues)} batches at once. Even more while you're away!`;
  } else if ('xpPerHour' in step) {
    line = `Room for ${String(step.capacity)} squishies, and they learn a little faster.`;
  } else if ('capacity' in step) {
    line = `Room for ${String(step.capacity)} squishies (now ${String(b.capacity ?? 0)}).`;
  }
  const after = building.levels[to];
  let next: string | null = null;
  if (after) {
    const fresh = Object.keys(after.cost).filter((id) => !(id in cost));
    const reach = 'safeRadius' in after ? ` reaches ${tiles(after.safeRadius)} and` : '';
    if (fresh.length > 0) {
      const what = fresh.map((id) => `${itemIcon(id)} ${itemName(id)}`).join(' and ');
      next = `Next time: Level ${String(to + 1)}${reach} needs ${what}.`;
    }
  }
  return {
    building,
    from: b.level,
    to,
    needs,
    affordable: needs.every((n) => n.ok),
    line,
    radius,
    next,
  };
}

/** A tile on the upgrade sheet's little map, in coordinates around the Heart Seed. */
export interface ReachTile {
  readonly q: number;
  readonly r: number;
  /** Safe already (home, or the fire's light now), safe after the upgrade, or still outside. */
  readonly state: 'now' | 'new' | 'outside';
}

/**
 * The little map on a fire's upgrade sheet: the home base and the land
 * around it, which tiles its light covers now and which it will after the
 * upgrade (shared rule: the fire's own tile plus its radius, and the whole
 * home base). Out to one ring past the furthest new tile.
 */
export function upgradeReach(home: HomeResponse, fire: MyBuilding, toRadius: number): ReachTile[] {
  const seed = home.tiles.find((t) => t.heartSeed) ?? { q: 0, r: 0 };
  const now = new Set([
    ...home.tiles.map((t) => hexKey(t)),
    ...hexSpiral({ q: fire.q, r: fire.r }, fire.safeRadius ?? 0).map(hexKey),
  ]);
  const after = new Set(hexSpiral({ q: fire.q, r: fire.r }, toRadius).map(hexKey));
  const ring = (h: { q: number; r: number }) => {
    const q = h.q - seed.q;
    const r = h.r - seed.r;
    return Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r));
  };
  const furthest = Math.max(
    1,
    ...hexSpiral({ q: fire.q, r: fire.r }, toRadius).map((h) => ring(h)),
  );
  return hexSpiral(seed, furthest + 1).map((h) => {
    const key = hexKey(h);
    const state = now.has(key) ? 'now' : after.has(key) ? 'new' : 'outside';
    return { q: h.q - seed.q, r: h.r - seed.r, state };
  });
}

/**
 * A home tile to build on, as the server's `spot` field wants it: a building
 * fills its whole ring tile (#589), so the spot is only which ring slot the
 * server records, the tile's first free one.
 */
export interface HomeSpot {
  readonly q: number;
  readonly r: number;
  readonly spot: number;
}

/**
 * Every free tile on my home base's ring, in `q, r` order, for the Build and
 * Move taps (#589: one building a tile, and it fills the tile). One entry a
 * tile, carrying the ring spot to send. The Heart Seed keeps the middle
 * (#604), so its tile is never offered, as the server refuses it. A tile
 * holding a building isn't free (shared `tileUseRefusal`, as the server
 * checks it, #537), and neither is the house's tile while the house stands
 * (#604, shared `houseTile` over the buildings as they stand). `except` is a
 * building being moved (its own tile counts as free); `slot` is the kind of
 * spot to send, a habitat's ring (#204).
 */
export function freeHomeTiles(
  home: HomeResponse,
  except: string | null = null,
  slot: BuildingSlot = 'ring',
): HomeSpot[] {
  const tiles = home.tiles.filter((t) => !t.heartSeed).sort((a, b) => a.q - b.q || a.r - b.r);
  // The house's tile as it's drawn now (the server reads it the same way).
  const house = home.house ? houseTile(home.tiles, home.buildings) : null;
  const others = home.buildings.filter((b) => b.id !== except);
  const free: HomeSpot[] = [];
  for (const tile of tiles) {
    if (house && house.q === tile.q && house.r === tile.r) continue;
    const taken = new Set(
      others.filter((b) => b.q === tile.q && b.r === tile.r).map((b) => b.spot),
    );
    const use = { id: hexKey(tile), q: tile.q, r: tile.r, homeSlot: 0, homestead: null };
    if (
      tileUseRefusal(
        { placement: 'home' },
        { ...use, nodeResource: tile.nodeResource },
        taken.size > 0,
      )
    ) {
      continue;
    }
    const spot = freeSpots(RULES, tile, taken, slot)[0];
    if (spot !== undefined) free.push({ q: tile.q, r: tile.r, spot });
  }
  return free;
}

/** Species the client can draw: the public table plus what the server sent along. */
export function speciesMap(home: Pick<HomeResponse, 'speciesDefs'>): Map<string, Species> {
  return new Map([...GAME_DATA.species, ...home.speciesDefs].map((s) => [s.id, s]));
}

/** A squishy's name: its nickname, else its species', else a friendly stand-in. */
export function squishyName(squishy: HomeSquishy, species: ReadonlyMap<string, Species>): string {
  return squishy.nickname ?? species.get(squishy.speciesId)?.name ?? 'Mystery squishy';
}

/** A squishy's rarity for its dot (#240), or null when the client has no row for its species. */
export function squishyRarity(
  squishy: HomeSquishy,
  species: ReadonlyMap<string, Species>,
): Rarity | null {
  return species.get(squishy.speciesId)?.rarity ?? null;
}

/** One line about a building on its card. */
export function buildingNote(b: MyBuilding): string {
  if (b.kind === 'hearthfire') {
    const nights = b.nightsLeft ?? 0;
    if (nights === 0) return "It's out. Add Emberwood to light it!";
    return `Lit! ${String(nights)} ${nights === 1 ? 'night' : 'nights'} of fuel left.`;
  }
  if (b.kind === 'factory') {
    return "Makes lots of things at once, even while you're away!";
  }
  if (b.kind === 'training-grounds') {
    const n = b.residents ?? 0;
    const room = b.capacity ?? 0;
    return `${String(n)} of ${String(room)} squishies are practicing. They learn a little every hour, even while you're away.`;
  }
  // A habitat (every kind is handled above or here).
  const living = b.residents ?? 0;
  const room = b.capacity ?? 0;
  if (living === 0) return `Room for ${String(room)} squishies.`;
  return `${String(living)} of ${String(room)} squishies live here.`;
}

/** Does a squishy like this habitat (matching element or feeling tag)? */
export function likesHabitat(squishy: HomeSquishy, buildingId: string): boolean {
  const building = BUILDING_DATA.get(buildingId);
  if (building?.kind !== 'habitat') return false;
  return (
    (building.tags.elements as readonly string[]).includes(squishy.element) ||
    (building.tags.feelings as readonly string[]).includes(squishy.feeling)
  );
}

/**
 * What taking a building down gives back, to show before the player says yes:
 * its refund (shared `removeRefund`) plus any fuel it hasn't burned. The
 * server works it out again when it happens.
 */
export function refundPreview(b: MyBuilding): Record<string, number> {
  const building = BUILDING_DATA.get(b.buildingId);
  if (!building) return {};
  const back = removeRefund(building, b.level, RULES);
  if (building.kind === 'hearthfire' && (b.nightsLeft ?? 0) > 0) {
    for (const [id, n] of Object.entries(fuelCost(building, b.nightsLeft ?? 0))) {
      back[id] = (back[id] ?? 0) + n;
    }
  }
  return back;
}
